import { useState, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { collection, getDocs } from "firebase/firestore";
import { ref, child, get } from "firebase/database";
import { db, rtdb } from "../firebase";
import Select, { components } from "react-select";

const DownloadReport = ({ onClose }) => {
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [type, setType] = useState("Login Time");
  const [isDownloading, setIsDownloading] = useState(false);

  const typeOptions = [
    { value: "Login Time", label: "Login Time" },
    { value: "Breaks Total", label: "Breaks Total" },
    { value: "Actual Production", label: "Actual Production" },
  ];

  const isAllSelected =
    employeeOptions.length > 0 &&
    selectedEmployees.length === employeeOptions.length - 1;

  const formatKey = (date) => {
    return new Date(date).toLocaleDateString("en-GB").replace(/\//g, "_");
  };

  const getDatesBetween = (start, end) => {
    const dates = [];
    let current = new Date(start);

    while (current <= new Date(end)) {
      dates.push(formatKey(current));
      current.setDate(current.getDate() + 1);
    }

    return dates;
  };

  const handleDownload = async () => {
    if (!startDate || !endDate) {
      alert("Please select dates");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      alert("Start date should be before End date");
      return;
    }

    setIsDownloading(true);

    try {
      const dateKeys = getDatesBetween(startDate, endDate);
      const employeeMap = {};

      const snapshots = await Promise.all(
        dateKeys.map((key) => get(child(ref(rtdb), key))),
      );

      snapshots.forEach((snap, index) => {
        if (!snap.exists()) return;

        const key = dateKeys[index];
        const data = snap.val();

        Object.entries(data).forEach(([empId, emp]) => {
          const selectedIds = selectedEmployees.map((emp) => emp.value);

          if (selectedIds.length > 0 && !selectedIds.includes(empId)) {
            return;
          }

          if (!employeeMap[empId]) {
            employeeMap[empId] = {
              EmployeeID: empId,
              Name: emp.Name || "-",
            };
          }

          let value = "-";

          if (type === "Login Time") value = emp.LoginTime || "-";
          else if (type === "Breaks Total") value = emp.breakstotal || "-";
          else if (type === "Actual Production") {
            const actualKey = Object.keys(emp).find(
              (k) => k.toLowerCase() === "actualproduction",
            );
            value = actualKey ? emp[actualKey] : "-";
          }

          employeeMap[empId][key] = value;
        });
      });

      const finalData = Object.values(employeeMap);

      // ✅ Fill missing dates
      dateKeys.forEach((date) => {
        finalData.forEach((emp) => {
          if (!emp[date]) emp[date] = "-";
        });
      });

      // ✅ Row-wise totals
      let orderedData = finalData.map((emp) => {
        const obj = {
          EmployeeID: emp.EmployeeID,
          Name: emp.Name,
        };

        let rowTotal = 0;

        dateKeys.forEach((date) => {
          const value = emp[date];
          obj[date] = value;

          if (type === "Actual Production") {
            const num = Number(value) || 0;
            rowTotal += num;
          }
        });

        if (type === "Actual Production") {
          obj["Total"] = rowTotal;
        }

        return obj;
      });

      // ✅ Column-wise totals + Grand total
      if (type === "Actual Production") {
        const totalRow = {
          EmployeeID: "TOTAL",
          Name: "",
        };

        let grandTotal = 0;

        dateKeys.forEach((date) => {
          let colTotal = 0;

          orderedData.forEach((emp) => {
            const val = Number(emp[date]) || 0;
            colTotal += val;
          });

          totalRow[date] = colTotal;
          grandTotal += colTotal;
        });

        totalRow["Total"] = grandTotal;

        orderedData.push(totalRow); // ✅ Add last row
      }

      // ✅ Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(orderedData);

      // ✅ Column width (include Total column)
      const colWidths = [
        { wch: 15 },
        { wch: 20 },
        ...dateKeys.map(() => ({ wch: 15 })),
        ...(type === "Actual Production" ? [{ wch: 15 }] : []),
      ];
      worksheet["!cols"] = colWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });

      saveAs(blob, `Employee_Report_${type}.xlsx`);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setIsDownloading(false);
    }
  };

  const fetchEmployees = useCallback(async () => {
    if (employeeOptions.length > 0) return;

    try {
      const querySnapshot = await getDocs(collection(db, "Employee_IDS"));
      const options = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();

        options.push({
          value: doc.id, // JSKMID004
          label: data.Name || doc.id,
        });
      });

      setEmployeeOptions([{ value: "all", label: "Select All" }, ...options]);
    } catch (err) {
      console.error(err);
    }
  }, [employeeOptions]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  return (
    <div style={overlay}>
      <div style={modal}>
        <h2 style={{ textAlign: "center", color: "#e87115" }}>
          Download Report
        </h2>

        <input
          disabled={isDownloading}
          style={inputStyle}
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <input
          disabled={isDownloading}
          style={inputStyle}
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />

        <Select
          isDisabled={isDownloading}
          options={typeOptions}
          value={typeOptions.find((opt) => opt.value === type)}
          onChange={(selected) => setType(selected.value)}
          placeholder="Select Type"
          styles={{
            control: (base) => ({
              ...base,
              borderRadius: "8px",
              padding: "2px",
              border: "1px solid #ddd",
            }),
          }}
        />

        <Select
          isDisabled={isDownloading}
          options={employeeOptions}
          isMulti
          placeholder="All Employees"
          value={selectedEmployees}
          onMenuOpen={fetchEmployees}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          styles={{
            control: (base) => ({
              ...base,
              borderRadius: "8px",
              border: "1px solid #ddd",
            }),
            menu: (base) => ({
              ...base,
              zIndex: 9999, // prevent overlap issues
            }),
            menuList: (base) => ({
              ...base,
              maxHeight: "150px", // ✅ HEIGHT CONTROL
              overflowY: "auto",
            }),
          }}
          onChange={(selected) => {
            if (!selected) {
              setSelectedEmployees([]);
              return;
            }

            if (selected.some((opt) => opt.value === "all")) {
              setSelectedEmployees(employeeOptions.slice(1));
            } else {
              setSelectedEmployees(selected);
            }
          }}
          formatValueLabel={(option, { context }) => {
            if (context === "value") {
              if (isAllSelected) {
                return "All Employees Selected";
              }
              return option.label;
            }
            return option.label;
          }}
          components={{
            MultiValue: ({ index, getValue, ...props }) => {
              const maxToShow = 2;
              const selected = getValue();

              if (isAllSelected) {
                return index === 0 ? (
                  <div style={{ marginLeft: "5px" }}>
                    All Employees Selected
                  </div>
                ) : null;
              }

              if (index < maxToShow) {
                return <components.MultiValue {...props} />;
              }

              if (index === maxToShow) {
                return (
                  <div style={{ marginLeft: "5px" }}>
                    +{selected.length - maxToShow} more
                  </div>
                );
              }

              return null;
            },
          }}
        />

        <div style={buttonRow}>
          <button
            style={primaryBtn}
            onClick={handleDownload}
            disabled={isDownloading}
          >
            {isDownloading ? "Downloading..." : "Download"}
          </button>
          <button style={cancelBtn} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const overlay = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 9999,
  backdropFilter: "blur(5px)",
};

const modal = {
  background: "#ffffff",
  padding: "25px 30px",
  borderRadius: "14px",
  width: "350px",
  display: "flex",
  flexDirection: "column",
  gap: "15px",
  boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
};

const inputStyle = {
  padding: "10px",
  borderRadius: "8px",
  border: "1px solid #ddd",
  fontSize: "14px",
  cursor: "pointer",
};

const buttonRow = {
  display: "flex",
  justifyContent: "space-between",
  marginTop: "10px",
};

const primaryBtn = {
  background: "#e87115",
  color: "#fff",
  border: "none",
  padding: "8px 14px",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: "600",
};

const cancelBtn = {
  background: "#f3f4f6",
  border: "none",
  padding: "8px 14px",
  borderRadius: "8px",
  cursor: "pointer",
};

export default DownloadReport;
